import React, { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import {
  Box,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Divider,
  Popover,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatQuote,
  Code,
  Link as LinkIcon,
  LinkOff,
  Highlight as HighlightIcon,
  FormatColorText,
  HorizontalRule,
  Undo,
  Redo,
} from "@mui/icons-material";

const FONT_SIZES = [
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
  "32px",
  "36px",
  "48px",
];

const TEXT_COLORS = [
  "#000000",
  "#1a1a2e",
  "#16213e",
  "#374151",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#ffffff",
];

const HIGHLIGHT_COLORS = [
  "#fef08a",
  "#bbf7d0",
  "#bfdbfe",
  "#fecaca",
  "#e9d5ff",
  "#fed7aa",
  "#a7f3d0",
  "#f5d0fe",
];

const CaseRichTextEditor = ({
  editorState,
  onEditorStateChange,
  toolbar,
  onSaveShortcut,
  placeholder = "Escribe aquí...",
}) => {
  const [colorAnchor, setColorAnchor] = useState(null);
  const [highlightAnchor, setHighlightAnchor] = useState(null);
  const colorBtnRef = useRef(null);
  const highlightBtnRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: "is-editor-empty",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: editorState || "",
    onUpdate: ({ editor: instance }) => {
      onEditorStateChange?.(instance.getHTML());
    },
    editorProps: {
      attributes: {
        class: "case-editor-content",
      },
      handleKeyDown: (_, event) => {
        if (!onSaveShortcut) return false;
        if (
          (event.ctrlKey || event.metaKey) &&
          event.key?.toLowerCase() === "s"
        ) {
          event.preventDefault();
          onSaveShortcut();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const incoming = editorState || "";
    const current = editor.getHTML();
    if (incoming !== current) {
      editor.commands.setContent(incoming, false);
    }
  }, [editorState, editor]);

  if (!editor) return null;

  const buttonStyle = {
    borderRadius: "8px",
    border: "1px solid #e5ebf3",
    width: 32,
    height: 32,
  };

  const renderAction = (
    title,
    icon,
    action,
    isActive = false,
    disabled = false,
  ) => (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={action}
          disabled={disabled}
          sx={{
            ...buttonStyle,
            bgcolor: isActive ? "#eef5ff" : "#fff",
            color: isActive ? "#2563eb" : "#44556c",
          }}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  // Detect current font size from the selection
  const currentFontSize = editor.getAttributes("textStyle").fontSize || "";

  const handleSetLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL del enlace:", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <Box sx={{ border: "1px solid #d8e1ee", borderRadius: "12px", p: 1.2 }}>
      {/* ── ROW 1: text style, heading, font size ────────────────────── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, pb: 0.8 }}>
        {renderAction(
          "Negrita (Ctrl+B)",
          <FormatBold fontSize="small" />,
          () => editor.chain().focus().toggleBold().run(),
          editor.isActive("bold"),
          !editor.can().chain().focus().toggleBold().run(),
        )}
        {renderAction(
          "Cursiva (Ctrl+I)",
          <FormatItalic fontSize="small" />,
          () => editor.chain().focus().toggleItalic().run(),
          editor.isActive("italic"),
          !editor.can().chain().focus().toggleItalic().run(),
        )}
        {renderAction(
          "Subrayado (Ctrl+U)",
          <FormatUnderlined fontSize="small" />,
          () => editor.chain().focus().toggleUnderline().run(),
          editor.isActive("underline"),
          !editor.can().chain().focus().toggleUnderline().run(),
        )}
        {renderAction(
          "Tachado",
          <FormatStrikethrough fontSize="small" />,
          () => editor.chain().focus().toggleStrike().run(),
          editor.isActive("strike"),
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.4 }} />

        {/* Heading selector */}
        <Tooltip title="Estilo de texto">
          <Select
            size="small"
            variant="outlined"
            value={
              editor.isActive("heading", { level: 1 })
                ? "h1"
                : editor.isActive("heading", { level: 2 })
                  ? "h2"
                  : editor.isActive("heading", { level: 3 })
                    ? "h3"
                    : "p"
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === "p") editor.chain().focus().setParagraph().run();
              else
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: Number(v[1]) })
                  .run();
            }}
            sx={{
              height: 32,
              fontSize: "13px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5ebf3" },
            }}>
            <MenuItem value="p" sx={{ fontSize: "13px" }}>
              Párrafo
            </MenuItem>
            <MenuItem value="h1" sx={{ fontSize: "13px", fontWeight: 700 }}>
              Título 1
            </MenuItem>
            <MenuItem value="h2" sx={{ fontSize: "13px", fontWeight: 700 }}>
              Título 2
            </MenuItem>
            <MenuItem value="h3" sx={{ fontSize: "13px", fontWeight: 700 }}>
              Título 3
            </MenuItem>
          </Select>
        </Tooltip>

        {/* Font size selector */}
        <Tooltip title="Tamaño de letra">
          <Select
            size="small"
            variant="outlined"
            displayEmpty
            value={currentFontSize}
            onChange={(e) => {
              const sz = e.target.value;
              if (!sz) editor.chain().focus().unsetFontSize().run();
              else editor.chain().focus().setFontSize(sz).run();
            }}
            sx={{
              height: 32,
              minWidth: 64,
              fontSize: "13px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5ebf3" },
            }}>
            <MenuItem value="" sx={{ fontSize: "12px", color: "#999" }}>
              Tamaño
            </MenuItem>
            {FONT_SIZES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: "13px" }}>
                {s.replace("px", "")}
              </MenuItem>
            ))}
          </Select>
        </Tooltip>
      </Box>

      {/* ── ROW 2: color, highlight, lists, align, extras ─────────────── */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.8,
          borderBottom: "1px solid #edf2f8",
          pb: 1,
          mb: 1,
        }}>
        {/* Text color */}
        <Tooltip title="Color de texto">
          <span>
            <IconButton
              ref={colorBtnRef}
              size="small"
              onClick={() =>
                setColorAnchor(colorAnchor ? null : colorBtnRef.current)
              }
              sx={{
                ...buttonStyle,
                bgcolor: "#fff",
                color: "#44556c",
                position: "relative",
              }}>
              <FormatColorText fontSize="small" />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 3,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 16,
                  height: 3,
                  borderRadius: 1,
                  bgcolor: editor.getAttributes("textStyle").color || "#000",
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        {/* Highlight color */}
        <Tooltip title="Resaltar texto">
          <span>
            <IconButton
              ref={highlightBtnRef}
              size="small"
              onClick={() =>
                setHighlightAnchor(
                  highlightAnchor ? null : highlightBtnRef.current,
                )
              }
              sx={{
                ...buttonStyle,
                bgcolor: editor.isActive("highlight") ? "#eef5ff" : "#fff",
                color: editor.isActive("highlight") ? "#2563eb" : "#44556c",
              }}>
              <HighlightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.4 }} />

        {renderAction(
          "Lista",
          <FormatListBulleted fontSize="small" />,
          () => editor.chain().focus().toggleBulletList().run(),
          editor.isActive("bulletList"),
        )}
        {renderAction(
          "Lista numerada",
          <FormatListNumbered fontSize="small" />,
          () => editor.chain().focus().toggleOrderedList().run(),
          editor.isActive("orderedList"),
        )}
        {renderAction(
          "Cita",
          <FormatQuote fontSize="small" />,
          () => editor.chain().focus().toggleBlockquote().run(),
          editor.isActive("blockquote"),
        )}
        {renderAction(
          "Código en línea",
          <Code fontSize="small" />,
          () => editor.chain().focus().toggleCode().run(),
          editor.isActive("code"),
        )}
        {renderAction(
          "Separador horizontal",
          <HorizontalRule fontSize="small" />,
          () => editor.chain().focus().setHorizontalRule().run(),
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.4 }} />

        {renderAction(
          "Alinear izquierda",
          <FormatAlignLeft fontSize="small" />,
          () => editor.chain().focus().setTextAlign("left").run(),
          editor.isActive({ textAlign: "left" }),
        )}
        {renderAction(
          "Centrar",
          <FormatAlignCenter fontSize="small" />,
          () => editor.chain().focus().setTextAlign("center").run(),
          editor.isActive({ textAlign: "center" }),
        )}
        {renderAction(
          "Alinear derecha",
          <FormatAlignRight fontSize="small" />,
          () => editor.chain().focus().setTextAlign("right").run(),
          editor.isActive({ textAlign: "right" }),
        )}
        {renderAction(
          "Justificar",
          <FormatAlignJustify fontSize="small" />,
          () => editor.chain().focus().setTextAlign("justify").run(),
          editor.isActive({ textAlign: "justify" }),
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.4 }} />

        {renderAction(
          "Enlace",
          <LinkIcon fontSize="small" />,
          handleSetLink,
          editor.isActive("link"),
        )}
        {editor.isActive("link") &&
          renderAction("Quitar enlace", <LinkOff fontSize="small" />, () =>
            editor.chain().focus().unsetLink().run(),
          )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.4 }} />

        {renderAction(
          "Deshacer (Ctrl+Z)",
          <Undo fontSize="small" />,
          () => editor.chain().focus().undo().run(),
          false,
          !editor.can().chain().focus().undo().run(),
        )}
        {renderAction(
          "Rehacer (Ctrl+Y)",
          <Redo fontSize="small" />,
          () => editor.chain().focus().redo().run(),
          false,
          !editor.can().chain().focus().redo().run(),
        )}
      </Box>

      <EditorContent editor={editor} />

      {/* ── Color popover ──────────────────────────────────────────────── */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box
          sx={{
            p: 1.2,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.6,
            maxWidth: 160,
          }}>
          {TEXT_COLORS.map((c) => (
            <Box
              key={c}
              onClick={() => {
                editor.chain().focus().setColor(c).run();
                setColorAnchor(null);
              }}
              sx={{
                width: 22,
                height: 22,
                borderRadius: "4px",
                bgcolor: c,
                cursor: "pointer",
                border: "1px solid #d1d5db",
                "&:hover": {
                  transform: "scale(1.18)",
                  transition: "transform 120ms",
                },
              }}
            />
          ))}
          <Box
            onClick={() => {
              editor.chain().focus().unsetColor().run();
              setColorAnchor(null);
            }}
            sx={{
              width: "100%",
              fontSize: "11px",
              textAlign: "center",
              color: "#666",
              cursor: "pointer",
              mt: 0.4,
              "&:hover": { color: "#000" },
            }}>
            Quitar color
          </Box>
        </Box>
      </Popover>

      {/* ── Highlight popover ──────────────────────────────────────────── */}
      <Popover
        open={Boolean(highlightAnchor)}
        anchorEl={highlightAnchor}
        onClose={() => setHighlightAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Box
          sx={{
            p: 1.2,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.6,
            maxWidth: 130,
          }}>
          {HIGHLIGHT_COLORS.map((c) => (
            <Box
              key={c}
              onClick={() => {
                editor.chain().focus().setHighlight({ color: c }).run();
                setHighlightAnchor(null);
              }}
              sx={{
                width: 22,
                height: 22,
                borderRadius: "4px",
                bgcolor: c,
                cursor: "pointer",
                border: "1px solid #d1d5db",
                "&:hover": {
                  transform: "scale(1.18)",
                  transition: "transform 120ms",
                },
              }}
            />
          ))}
          <Box
            onClick={() => {
              editor.chain().focus().unsetHighlight().run();
              setHighlightAnchor(null);
            }}
            sx={{
              width: "100%",
              fontSize: "11px",
              textAlign: "center",
              color: "#666",
              cursor: "pointer",
              mt: 0.4,
              "&:hover": { color: "#000" },
            }}>
            Quitar resaltado
          </Box>
        </Box>
      </Popover>

      <style>{`
        .case-editor-content {
          min-height: 170px;
          padding: 8px 6px;
          color: #2f3f54;
          line-height: 1.58;
          outline: none;
        }
        .case-editor-content p {
          margin: 0 0 0.45rem;
        }
        .case-editor-content h1 { font-size: 1.7em; font-weight: 700; margin: 0.5rem 0 0.3rem; }
        .case-editor-content h2 { font-size: 1.35em; font-weight: 700; margin: 0.5rem 0 0.3rem; }
        .case-editor-content h3 { font-size: 1.1em; font-weight: 700; margin: 0.4rem 0 0.2rem; }
        .case-editor-content blockquote {
          border-left: 3px solid #c5d3e0;
          padding-left: 12px;
          color: #5a7085;
          margin: 0.4rem 0;
        }
        .case-editor-content code {
          background: #f0f4f8;
          border-radius: 4px;
          padding: 1px 5px;
          font-family: monospace;
          font-size: 0.9em;
        }
        .case-editor-content a {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
        }
        .case-editor-content hr {
          border: none;
          border-top: 1px solid #d1d9e4;
          margin: 0.7rem 0;
        }
        .case-editor-content p.is-editor-empty:first-child::before,
        .case-editor-content p.is-empty::before {
          color: #8ca0b6;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .case-editor-content ul,
        .case-editor-content ol {
          margin: 0.35rem 0 0.6rem 1.1rem;
        }
      `}</style>
    </Box>
  );
};

export default CaseRichTextEditor;
